/**
 * Teams tab — distinct teams derived from `Player.team_id`/`team_name` per
 * league. Backed by `GET /v1/admin/teams` which already aggregates Player
 * rows server-side; the admin panel doesn't need a standalone Team list.
 *
 * Split out from the legacy combined Teams+Players tab so each Game Data
 * dataset has its own first-class navigation entry.
 */

import { useEffect, useState } from 'react';

import { Input } from '@/components/ui/input';

import { type AdminTeamRow, type LeagueName, useAdminTeams } from './api';
import { DASH, DataTable, DataTableShell } from './shared';

export function TeamsTab({ league }: { league: LeagueName }) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data, isLoading, error } = useAdminTeams(debouncedQ || undefined, league);
  const rows = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="min-w-[280px] flex-1">
          <Input
            placeholder="Search team name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="text-xs text-muted-foreground">{rows.length} teams</span>
      </div>

      <DataTableShell loading={isLoading} error={error} empty={rows.length === 0}>
        <DataTable<AdminTeamRow>
          rows={rows}
          rowKey={(r) => r.team_id}
          columns={[
            {
              key: 'name',
              label: 'Name',
              render: (r) => <span className="font-medium text-foreground">{r.name}</span>,
            },
            { key: 'short_name', label: 'Short name', render: (r) => r.short_name },
            {
              key: 'external_id',
              label: 'External ID',
              render: (r) => <code className="text-xs">{r.external_id}</code>,
            },
            {
              key: 'player_count',
              label: 'Player count',
              align: 'right',
              render: (r) => r.player_count,
            },
            {
              key: 'logo_url',
              label: 'Logo URL',
              render: (r) =>
                r.logo_url ? (
                  <a
                    className="font-mono text-xs text-primary hover:underline"
                    href={r.logo_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {r.logo_url}
                  </a>
                ) : (
                  DASH
                ),
            },
          ]}
        />
      </DataTableShell>
    </div>
  );
}
